import React from "react";
import { ProgramTheme } from "@/lib/types/types";
import { getCustomCss } from "@/lib/ProgramTheme/programThemeGetters";
import { getInstallType } from "@/lib/ServerApi/ApiUrls";
import { CustomCssContext, CustomCssContextEmpty } from "./CustomCssContext";

// This is a workaround to allow the use of css variables in styled-components
declare module "react" {
  interface CSSProperties {
    [key: `--${string}`]: string | number;
  }
}

interface CustomCssContextProviderProps {
  programTheme?: ProgramTheme;
  children?: React.ReactNode;
}

const CustomCssContextProvider: React.FC<CustomCssContextProviderProps> = ({
  programTheme,
  children,
}) => {
  const removePollutingStyles = (styles: string): string => {
    if (window.self === window.top) {
      return styles
        .replace(/body,/g, "")
        .replace(/label,/g, "")
        .replace(/button\[type=('|")submit('|")\]\s*?\{[\s\S]*?\}\n?/g, "") // button type submit overwrites
        .replace(/a:link,\s+a:visited,\s+a:hover,\s+a:active\s*?\{[\s\S]*?\}\n?/g, "") // links overwrites
        .replace(/h1\s*?\{[\s\S]*?\}\n?/g, ""); // h1 overwrites
    }
    return styles;
  };

  const styles = getCustomCss(programTheme);
  const customStyles = styles ? removePollutingStyles(styles) : null;

  const landingPage = programTheme?.themeChoices?.landingPage;
  const themeChoices = programTheme?.themeChoices;

  const landingBackgroundColor =
    landingPage?.backgroundColor || CustomCssContextEmpty.landingBackgroundColor;
  const landingPrimaryFontColor =
    landingPage?.primaryFontColor || CustomCssContextEmpty.landingPrimaryFontColor;
  const landingBackgroundImage =
    landingPage?.backgroundImageUrl || CustomCssContextEmpty.landingBackgroundImage;

  const formBackgroundColor =
    themeChoices?.backgroundColor || CustomCssContextEmpty.formBackgroundColor;
  const formPrimaryFontColor =
    themeChoices?.primaryFontColor || CustomCssContextEmpty.formPrimaryFontColor;
  const formButtonColor = themeChoices?.buttonColor || CustomCssContextEmpty.formButtonColor;
  const formButtonFontColor =
    themeChoices?.buttonFontColor || CustomCssContextEmpty.formButtonFontColor;
  const formLinkColor = themeChoices?.linkColor || CustomCssContextEmpty.formLinkColor;
  const formH1FontColor = themeChoices?.h1FontColor || CustomCssContextEmpty.formH1FontColor;
  const formHelperFontColor =
    themeChoices?.helperFontColor || CustomCssContextEmpty.formHelperFontColor;
  const formFont = themeChoices?.font || CustomCssContextEmpty.formFont;

  const cssVariables = {
    "--sheerid-landing-background-color": landingBackgroundColor,
    "--sheerid-landing-primary-font-color": landingPrimaryFontColor,
    "--sheerid-landing-background-image-url":
      landingBackgroundImage === CustomCssContextEmpty.landingBackgroundImage
        ? CustomCssContextEmpty.landingBackgroundImage
        : `url(${landingBackgroundImage})`,
    "--sheerid-color-bg": formBackgroundColor,
    "--sheerid-color-fg": formPrimaryFontColor,
    "--sheerid-form-button-color": formButtonColor,
    "--sheerid-form-button-font-color": formButtonFontColor,
    "--sheerid-form-link-color": formLinkColor,
    "--sheerid-form-h1-font-color": formH1FontColor,
    "--sheerid-form-helper-font-color": formHelperFontColor,
    "--sheerid-font-family-body":
      formFont === CustomCssContextEmpty.formFont
        ? CustomCssContextEmpty.formFont
        : `${formFont}, var(--sheerid-font-family-sans)`,
    "--sheerid-color-fg-05":
      "color-mix(in oklab, var(--sheerid-color-bg) 95%, var(--sheerid-color-fg) 5%)",
    "--sheerid-color-fg-10":
      "color-mix(in oklab, var(--sheerid-color-bg) 90%, var(--sheerid-color-fg) 10%)",
    "--sheerid-color-fg-20":
      "color-mix(in oklab, var(--sheerid-color-bg) 80%, var(--sheerid-color-fg) 20%)",
    "--sheerid-color-fg-30":
      "color-mix(in oklab, var(--sheerid-color-bg) 70%, var(--sheerid-color-fg) 30%)",
    "--sheerid-color-fg-40":
      "color-mix(in oklab, var(--sheerid-color-bg) 60%, var(--sheerid-color-fg) 40%)",
    "--sheerid-color-fg-50":
      "color-mix(in oklab, var(--sheerid-color-bg) 50%, var(--sheerid-color-fg) 50%)",
    "--sheerid-color-fg-60":
      "color-mix(in oklab, var(--sheerid-color-bg) 40%, var(--sheerid-color-fg) 60%)",
    "--sheerid-color-fg-70":
      "color-mix(in oklab, var(--sheerid-color-bg) 30%, var(--sheerid-color-fg) 70%)",
    "--sheerid-color-fg-80":
      "color-mix(in oklab, var(--sheerid-color-bg) 20%, var(--sheerid-color-fg) 80%)",
    "--sheerid-color-fg-90":
      "color-mix(in oklab, var(--sheerid-color-bg) 10%, var(--sheerid-color-fg) 90%)",
    "--sheerid-color-fg-t05": "color-mix(in oklab, transparent 95%, var(--sheerid-color-fg) 5%)",
    "--sheerid-color-fg-t10": "color-mix(in oklab, transparent 90%, var(--sheerid-color-fg) 10%)",
    "--sheerid-color-fg-t20": "color-mix(in oklab, transparent 80%, var(--sheerid-color-fg) 20%)",
    "--sheerid-color-fg-t30": "color-mix(in oklab, transparent 70%, var(--sheerid-color-fg) 30%)",
    "--sheerid-color-fg-t40": "color-mix(in oklab, transparent 60%, var(--sheerid-color-fg) 40%)",
    "--sheerid-color-fg-t50": "color-mix(in oklab, transparent 50%, var(--sheerid-color-fg) 50%)",
    "--sheerid-color-fg-t60": "color-mix(in oklab, transparent 40%, var(--sheerid-color-fg) 60%)",
    "--sheerid-color-fg-t70": "color-mix(in oklab, transparent 30%, var(--sheerid-color-fg) 70%)",
    "--sheerid-color-fg-t80": "color-mix(in oklab, transparent 20%, var(--sheerid-color-fg) 80%)",
    "--sheerid-color-fg-t90": "color-mix(in oklab, transparent 10%, var(--sheerid-color-fg) 90%)",
  };

  const container = React.useRef<HTMLDivElement>(null);
  const isInIframe = window.top !== window.self;
  const isModal = getInstallType() === "cdn_modal_iframe";

  return (
    <CustomCssContext.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        landingBackgroundColor,
        landingPrimaryFontColor,
        landingBackgroundImage,
        formBackgroundColor,
        formPrimaryFontColor,
        formButtonColor,
        formButtonFontColor,
        formLinkColor,
        formH1FontColor,
        formHelperFontColor,
        formFont,

        cssVariables,
        customStyles,
      }}
    >
      <div style={cssVariables} ref={container} data-in-iframe={isInIframe} data-in-modal={isModal}>
        {customStyles && <style>{customStyles}</style>}

        {children}
      </div>
    </CustomCssContext.Provider>
  );
};

export default CustomCssContextProvider;
